import { useState, useEffect, useRef } from 'react';

interface PriceRangeSliderProps {
  minPrice: number;
  maxPrice: number;
  onPriceChange: (min: string, max: string) => void;
  className?: string;
}

export default function PriceRangeSlider({ minPrice, maxPrice, onPriceChange, className = '' }: PriceRangeSliderProps) {
  // Use 0 and maxPrice as defaults to avoid triggering onPriceChange on mount
  // Only update when user actually interacts
  const [selectedMin, setSelectedMin] = useState<number>(0);
  const [selectedMax, setSelectedMax] = useState<number>(maxPrice || 1000000);
  const [debouncedMin, setDebouncedMin] = useState<number>(0);
  const [debouncedMax, setDebouncedMax] = useState<number>(maxPrice || 1000000);
  
  // Track if user has ever interacted with the slider
  const userHasInteracted = useRef(false);

  // Only trigger onPriceChange when user has actually interacted with the slider
  // This prevents the double refresh when component mounts with URL price params
  useEffect(() => {
    // Skip if user hasn't interacted yet (i.e., on initial mount)
    if (!userHasInteracted.current) {
      return;
    }
    
    const timer = setTimeout(() => {
      // Only trigger if values actually changed from last debounced values
      if (debouncedMin !== selectedMin || debouncedMax !== selectedMax) {
        onPriceChange(selectedMin.toString(), selectedMax.toString());
        setDebouncedMin(selectedMin);
        setDebouncedMax(selectedMax);
      }
    }, 500); // 500ms debounce delay
    
    return () => clearTimeout(timer);
  }, [selectedMin, selectedMax, debouncedMin, debouncedMax, onPriceChange]);


  const handleMinChange = (value: number) => {
    userHasInteracted.current = true;
    setSelectedMin(value);
  };

  const handleMaxChange = (value: number) => {
    userHasInteracted.current = true;
    setSelectedMax(value);
  };

  // Quick preset handler - only update state, let debounce handle onPriceChange
  const handlePreset = (min: number, max: number) => {
    userHasInteracted.current = true;
    setSelectedMin(min);
    setSelectedMax(max);
    // Debounce will handle onPriceChange
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Range slider display */}
      <div className="px-1">
        <div className="flex justify-between text-xs mb-2" style={{ color: 'var(--glass-text-secondary, #898989)' }}>
          <span>€ {selectedMin.toLocaleString('it-IT')}</span>
          <span>€ {selectedMax.toLocaleString('it-IT')}</span>
        </div>
        {/* Min slider */}
        <div className="mb-3">
          <label className="block text-xs mb-1" style={{ color: 'var(--glass-text-secondary, #898989)' }}>Min: €{selectedMin.toLocaleString('it-IT')}</label>
          <input
            type="range"
            min={minPrice || 0}
            max={maxPrice || 1000000}
            step={1000}
            value={selectedMin}
            onChange={(e) => handleMinChange(parseInt(e.target.value))}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer"
            style={{ 
              background: 'var(--glass-slider-track, #e5e7eb)',
              accentColor: 'var(--glass-primary, #0099ff)'
            }}
          />
        </div>
        {/* Max slider */}
        <div>
          <label className="block text-xs mb-1" style={{ color: 'var(--glass-text-secondary, #898989)' }}>Max: €{selectedMax.toLocaleString('it-IT')}</label>
          <input
            type="range"
            min={minPrice || 0}
            max={maxPrice || 1000000}
            step={1000}
            value={selectedMax}
            onChange={(e) => handleMaxChange(parseInt(e.target.value))}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer"
            style={{ 
              background: 'var(--glass-slider-track, #e5e7eb)',
              accentColor: 'var(--glass-primary, #0099ff)'
            }}
          />
        </div>
      </div>

      {/* Quick presets */}
      <div className="grid grid-cols-2 gap-2 mt-3">
        <button
          type="button"
          onClick={() => handlePreset(0, 10000)}
          className="px-2 py-1 text-xs rounded transition-colors"
          style={{ 
            background: 'var(--glass-button-secondary-bg, #f3f4f6)',
            color: 'var(--glass-text-primary, #242424)'
          }}
        >
          Fino a €10k
        </button>
        <button
          type="button"
          onClick={() => handlePreset(10000, 50000)}
          className="px-2 py-1 text-xs rounded transition-colors"
          style={{ 
            background: 'var(--glass-button-secondary-bg, #f3f4f6)',
            color: 'var(--glass-text-primary, #242424)'
          }}
        >
          €10k - €50k
        </button>
        <button
          type="button"
          onClick={() => handlePreset(50000, 100000)}
          className="px-2 py-1 text-xs rounded transition-colors"
          style={{ 
            background: 'var(--glass-button-secondary-bg, #f3f4f6)',
            color: 'var(--glass-text-primary, #242424)'
          }}
        >
          €50k - €100k
        </button>
        <button
          type="button"
          onClick={() => handlePreset(100000, maxPrice || 1000000)}
          className="px-2 py-1 text-xs rounded transition-colors"
          style={{ 
            background: 'var(--glass-button-secondary-bg, #f3f4f6)',
            color: 'var(--glass-text-primary, #242424)'
          }}
        >
          Oltre €100k
        </button>
      </div>
    </div>
  );
}
